import { TextInput } from '@/components/atoms'
import { preventInvalidNumericInput } from '@/utils/inputValidations'

/**
 * InventoryFieldsRowForCardTypeBoth component - Reusable component for quota, leftover inventory, and limit per store fields
 * @param {Object} props - Component props
 * @param {Object} props.formData - Form data containing quota, leftoverInventory, limitPerStore
 * @param {Function} props.handleQuotaDigitalCardChange - Function to handle quota digital card field changes
 * @param {Function} props.handleLeftoverInventoryDigitalCardChange - Function to handle leftover inventory digital card field changes
 * @param {Function} props.handleLimitPerStoreDigitalCardChange - Function to handle limit per store digital card field changes
 * @param {Function} props.handleQuotaPhysicalCardChange - Function to handle quota physical card field changes
 * @param {Function} props.handleLeftoverInventoryPhysicalCardChange - Function to handle leftover inventory physical card field changes
 * @param {Function} props.handleLimitPerStorePhysicalCardChange - Function to handle limit per store physical card field changes
 * @param {boolean} props.isQuotaDigitalCardDisabled - Whether quota digital card field should be disabled
 * @param {boolean} props.isQuotaPhysicalCardDisabled - Whether quota physical card field should be disabled
 * @param {string} props.quotaDigitalCardValidationError - Validation error message for quota digital card field
 * @param {string} props.quotaPhysicalCardValidationError - Validation error message for quota physical card field
 * @param {string} props.limitPerStoreDigitalCardValidationError - Validation error message for limit per store digital card field
 * @param {string} props.limitPerStorePhysicalCardValidationError - Validation error message for limit per store physical card field
 * @returns {JSX.Element} The rendered InventoryFieldsRowForCardTypeBoth component
 */

const renderTextInput = ({
  id,
  label,
  value,
  onChange,
  placeholderText,
  className = 'text-sm',
  isDark = true,
  type = 'text',
  disabled = false,
  handleKeyDown = preventInvalidNumericInput,
  error,
}) => (
  <div className="w-full md:w-[262px]">
    <TextInput
      id={id}
      label={label}
      value={value}
      onChange={onChange}
      placeholderText={placeholderText}
      className={className}
      isDark={isDark}
      type={type}
      disabled={disabled}
      handleKeyDown={handleKeyDown}
      error={error}
    />
  </div>
)

const InventoryFieldsRowForCardTypeBoth = ({
  formData,
  handleQuotaDigitalCardChange,
  handleLeftoverInventoryDigitalCardChange,
  handleLimitPerStoreDigitalCardChange,
  handleQuotaPhysicalCardChange,
  handleLeftoverInventoryPhysicalCardChange,
  handleLimitPerStorePhysicalCardChange,
  isQuotaDigitalCardDisabled,
  isQuotaPhysicalCardDisabled,
  quotaDigitalCardValidationError,
  quotaPhysicalCardValidationError,
  limitPerStoreDigitalCardValidationError,
  limitPerStorePhysicalCardValidationError
}) => {

  return (
    <div>
      <div className="flex flex-col items-start gap-6 md:flex-row">
        {renderTextInput({
          id: 'quota-digital-card',
          label: 'Quota Digital Card*',
          value: formData?.quotaDigitalCard,
          onChange: handleQuotaDigitalCardChange,
          placeholderText: 'Add Quota',
          disabled: isQuotaDigitalCardDisabled,
          error: quotaDigitalCardValidationError,
        })}

        {renderTextInput({
          label: 'Leftover Inventory - Digital Card',
          value: formData?.leftoverInventoryDigitalCard,
          onChange: handleLeftoverInventoryDigitalCardChange,
          placeholderText: 'Leftover Inventory',
          disabled: true,
        })}

        {renderTextInput({
          label: 'Limit per Store - Digital Card',
          value: formData?.limitPerStoreDigitalCard,
          onChange: handleLimitPerStoreDigitalCardChange,
          placeholderText: 'Limit Account',
          error: limitPerStoreDigitalCardValidationError,
        })}
      </div>

      <div className="flex flex-col items-start gap-6 md:flex-row">
        {renderTextInput({
          id: 'quota-physical-card',
          label: 'Quota Physical Card*',
          value: formData?.quotaPhysicalCard,
          onChange: handleQuotaPhysicalCardChange,
          placeholderText: 'Add Quota',
          disabled: isQuotaPhysicalCardDisabled,
          error: quotaPhysicalCardValidationError,
        })}

        {renderTextInput({
          label: 'Leftover Inventory - Physical Card',
          value: formData?.leftoverInventoryPhysicalCard,
          onChange: handleLeftoverInventoryPhysicalCardChange,
          placeholderText: 'Leftover Inventory',
          disabled: true,
        })}

        {renderTextInput({
          label: 'Limit per Store - Physical Card',
          value: formData?.limitPerStorePhysicalCard,
          onChange: handleLimitPerStorePhysicalCardChange,
          placeholderText: 'Limit Account',
          error: limitPerStorePhysicalCardValidationError,
        })}
      </div>
    </div>
  )
}

export default InventoryFieldsRowForCardTypeBoth
